/**
 * @module useLruCache
 * @description This module provides a `useLruCache` hook, which implements a Least Recently Used (LRU) cache.
 * It uses a Map for O(1) key-based lookups and a doubly linked list to maintain the order of usage, ensuring that get, set, and eviction operations are all efficient.
 *
 * @description このモジュールは、Least Recently Used (LRU) キャッシュを実装した `useLruCache` フックを提供します。
 * Map を使用した O(1) のキー検索と、使用順序を維持するための双方向連結リストを組み合わせることで、get, set, および削除操作のすべてを効率的に行います。
 */
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * @class DoublyLinkedListNode
 * @description Represents a node in the doubly linked list. It holds a key-value pair and references to the previous and next nodes.
 * @description 双方向連結リスト内のノードを表します。キーと値のペア、および前後のノードへの参照を保持します。
 */
class DoublyLinkedListNode<K, V> {
    key: K
    value: V
    prev: DoublyLinkedListNode<K, V> | null = null
    next: DoublyLinkedListNode<K, V> | null = null

    constructor(key: K, value: V) {
        // キーを初期化
        this.key = key
        // 値を初期化
        this.value = value
    }
}

/**
 * @class DoublyLinkedList
 * @description Implements a doubly linked list to maintain the usage order of cache items.
 * The tail of the list represents the most recently used item, and the head represents the least recently used item.
 * @description キャッシュアイテムの使用順序を維持するための双方向連結リストを実装します。
 * リストの末尾が最も最近使用されたアイテムを、先頭が最も最近使用されていないアイテムを示します。
 */
class DoublyLinkedList<K, V> {
    private head: DoublyLinkedListNode<K, V> | null = null
    private tail: DoublyLinkedListNode<K, V> | null = null

    /**
     * @method addToTail
     * @description Adds a node to the tail of the list, marking it as the most recently used.
     * @description ノードをリストの末尾に追加し、最も最近使用されたものとしてマークします。
     * @param {DoublyLinkedListNode<K, V>} node - The node to add.
     */
    addToTail(node: DoublyLinkedListNode<K, V>) {
        // リストが既に末尾ノードを持っているかチェック
        if (this.tail) {
            // 既存の末尾ノードの次に新しいノードをリンク
            this.tail.next = node
            // 新しいノードの前に既存の末尾ノードをリンク
            node.prev = this.tail
            // リストの末尾を新しいノードに更新
            this.tail = node
        } else {
            // リストが空の場合、新しいノードが先頭かつ末尾となる
            this.head = this.tail = node
        }
    }

    /**
     * @method remove
     * @description Removes a given node from the list.
     * @description 指定されたノードをリストから削除します。
     * @param {DoublyLinkedListNode<K, V>} node - The node to remove.
     */
    remove(node: DoublyLinkedListNode<K, V>) {
        // ノードに前のノードが存在する場合
        if (node.prev) {
            // 前のノードの `next` を、現在のノードの `next` につなぎ直す
            node.prev.next = node.next
        } else {
            // ノードが先頭の場合、リストの先頭を次のノードに更新
            this.head = node.next
        }

        // ノードに次のノードが存在する場合
        if (node.next) {
            // 次のノードの `prev` を、現在のノードの `prev` につなぎ直す
            node.next.prev = node.prev
        } else {
            // ノードが末尾の場合、リストの末尾を前のノードに更新
            this.tail = node.prev
        }

        // 削除されたノードの参照をクリア
        node.prev = null
        node.next = null
    }

    /**
     * @method removeHead
     * @description Removes and returns the head of the list, which is the least recently used item.
     * @description リストの先頭（最も最近使用されていないアイテム）を削除して返します。
     * @returns {DoublyLinkedListNode<K, V> | null} The removed head node, or null if the list is empty.
     */
    removeHead(): DoublyLinkedListNode<K, V> | null {
        // 現在の先頭ノードを保持
        const head = this.head
        // 先頭ノードが存在する場合のみ処理
        if (head) {
            // 先頭ノードをリストから削除
            this.remove(head)
        }
        // 削除したノード（または null）を返す
        return head
    }

    /**
     * @method moveToTail
     * @description Moves an existing node to the tail of the list to mark it as most recently used.
     * @description 既存のノードをリストの末尾に移動し、最も最近使用されたものとしてマークします。
     * @param {DoublyLinkedListNode<K, V>} node - The node to move.
     */
    moveToTail(node: DoublyLinkedListNode<K, V>) {
        // ノードを現在の位置から一旦削除
        this.remove(node)
        // ノードをリストの末尾に再追加
        this.addToTail(node)
    }
}

/**
 * @hook useLruCache
 * @description A custom hook that provides a Least Recently Used (LRU) cache of a specified capacity.
 * It returns an object with methods to interact with the cache (`get`, `set`, `has`, `clear`).
 * @description 指定された容量の LRU (Least Recently Used) キャッシュを提供するカスタムフック。
 * キャッシュを操作するためのメソッド (`get`, `set`, `has`, `clear`) を持つオブジェクトを返します。
 * @param {number} capacity - The maximum capacity of the cache.
 * @returns {{ get: (key: K) => V | undefined, set: (key: K, value: V) => void, has: (key: K) => boolean, clear: () => void }} An object with methods to interact with the cache.
 */
export function useLruCache<K, V>(capacity: number) {
    // キャッシュストレージ。キーと双方向連結リストのノードをマッピングする (O(1) アクセス用)
    const cache = useRef(new Map<K, DoublyLinkedListNode<K, V>>())
    // 使用順序を管理する双方向連結リスト (先頭が最も古く、末尾が最も新しい)
    const list = useRef(new DoublyLinkedList<K, V>())

    useEffect(() => {
        while (cache.current.size > capacity) {
            const lruNode = list.current.removeHead()
            if (lruNode) {
                cache.current.delete(lruNode.key)
            } else {
                // This should not happen if cache.current.size > 0
                break
            }
        }
    }, [capacity])

    /**
     * @function get
     * @description Retrieves a value from the cache for a given key. If found, the item is marked as most recently used.
     * @description 指定されたキーの値を取得します。アイテムが見つかった場合、それは最も最近使用されたものとしてマークされます。
     * @param {K} key - The key of the item to retrieve.
     * @returns {V | undefined} The cached value, or undefined if the key does not exist.
     */
    const get = useCallback((key: K): V | undefined => {
        // Map からノードを効率的に検索
        const node = cache.current.get(key)
        // ノードが存在する場合
        if (node) {
            // アクセスされたノードを使用順序リストの末尾に移動
            list.current.moveToTail(node)
            // ノードの値を返す
            return node.value
        }
        // ノードが存在しない場合は undefined を返す
        return undefined
    }, [])

    /**
     * @function set
     * @description Adds or updates a key-value pair in the cache. If the cache is full, it evicts the least recently used item.
     * @description キーと値のペアをキャッシュに追加または更新します。キャッシュが満杯の場合、最も最近使用されていないアイテムが削除されます。
     * @param {K} key - The key of the item to set.
     * @param {V} value - The value of the item to set.
     */
    const set = useCallback(
        (key: K, value: V) => {
            if (capacity <= 0) {
                return
            }
            // キャッシュ内にキーが既に存在するかチェック
            let node = cache.current.get(key)

            if (node) {
                // キーが存在する場合、値を更新
                node.value = value
                // アクセスされたので、使用順序リストの末尾に移動
                list.current.moveToTail(node)
            } else {
                // 新しいノードを追加する場合
                // キャッシュが容量上限に達しているかチェック
                if (cache.current.size >= capacity) {
                    // 容量オーバーの場合、最も最近使用されていないノード (リストの先頭) を取得して削除
                    const lruNode = list.current.removeHead()
                    if (lruNode) {
                        // Map からも対応するエントリを削除
                        cache.current.delete(lruNode.key)
                    }
                }
                // 新しいノードを作成
                node = new DoublyLinkedListNode(key, value)
                // 新しいノードを Map に登録
                cache.current.set(key, node)
                // 新しいノードを使用順序リストの末尾に追加
                list.current.addToTail(node)
            }
        },
        [capacity],
    )

    /**
     * @function has
     * @description Checks if a key exists in the cache.
     * @description 指定されたキーがキャッシュ内に存在するかどうかを確認します。
     * @param {K} key - The key to check.
     * @returns {boolean} True if the key exists, false otherwise.
     */
    const has = useCallback((key: K): boolean => {
        // Map にキーが存在するかどうかを O(1) でチェック
        return cache.current.has(key)
    }, [])

    /**
     * @function clear
     * @description Clears the entire cache, removing all entries.
     * @description キャッシュを完全にクリアし、すべてのエントリを削除します。
     */
    const clear = useCallback(() => {
        // Map をクリア
        cache.current.clear()
        // 双方向連結リストを新しく作成してリセット
        list.current = new DoublyLinkedList<K, V>()
    }, [])

    const [handler, setHandler] = useState(() => ({ get, set, has, clear }))

    useEffect(() => setHandler({ get, set, has, clear }), [get, set, has, clear])

    // キャッシュ操作用の API を返す
    return handler
}
